import { useState, useCallback, useEffect, useRef } from 'react'

export interface UseCarouselOptions {
	totalSlides: number
	autoPlay?: boolean
	autoPlayInterval?: number
	loop?: boolean
	onSlideChange?: (index: number) => void
}

export interface UseCarouselReturn {
	currentIndex: number
	isTransitioning: boolean
	isPaused: boolean
	goToNext: () => void
	goToPrevious: () => void
	goToSlide: (index: number) => void
	pause: () => void
	play: () => void
	toggle: () => void
	canGoNext: boolean
	canGoPrevious: boolean
}

export function useCarousel({
	totalSlides,
	autoPlay = false,
	autoPlayInterval = 5000,
	loop = true,
	onSlideChange,
}: UseCarouselOptions): UseCarouselReturn {
	const [currentIndex, setCurrentIndex] = useState(0)
	const [isTransitioning, setIsTransitioning] = useState(false)
	const [isPaused, setIsPaused] = useState(false)
	const intervalRef = useRef<NodeJS.Timeout | undefined>()
	const transitionTimeoutRef = useRef<NodeJS.Timeout | undefined>()

	// Calculate navigation availability
	const canGoNext = loop ? true : currentIndex < totalSlides - 1
	const canGoPrevious = loop ? true : currentIndex > 0

	// Handle slide change with transition management
	const handleSlideChange = useCallback(
		(newIndex: number, transitionDuration = 600) => {
			if (
				isTransitioning ||
				newIndex === currentIndex ||
				newIndex < 0 ||
				newIndex >= totalSlides
			) {
				return
			}

			setIsTransitioning(true)
			setCurrentIndex(newIndex)
			onSlideChange?.(newIndex)

			// Clear any existing transition timeout
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current)
			}

			// Reset transition state after animation completes
			transitionTimeoutRef.current = setTimeout(() => {
				setIsTransitioning(false)
			}, transitionDuration) as NodeJS.Timeout
		},
		[currentIndex, isTransitioning, totalSlides, onSlideChange],
	)

	// Navigation functions
	const goToNext = useCallback(() => {
		if (!canGoNext) return
		const nextIndex =
			loop && currentIndex === totalSlides - 1 ? 0 : currentIndex + 1
		handleSlideChange(nextIndex)
	}, [currentIndex, totalSlides, loop, canGoNext, handleSlideChange])

	const goToPrevious = useCallback(() => {
		if (!canGoPrevious) return
		const prevIndex =
			loop && currentIndex === 0 ? totalSlides - 1 : currentIndex - 1
		handleSlideChange(prevIndex)
	}, [currentIndex, totalSlides, loop, canGoPrevious, handleSlideChange])

	const goToSlide = useCallback(
		(index: number) => {
			handleSlideChange(index)
		},
		[handleSlideChange],
	)

	// Play/pause controls
	const pause = useCallback(() => {
		setIsPaused(true)
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = undefined
		}
	}, [])

	const play = useCallback(() => {
		setIsPaused(false)
	}, [])

	const toggle = useCallback(() => {
		if (isPaused) {
			play()
		} else {
			pause()
		}
	}, [isPaused, play, pause])

	// Auto-play functionality
	useEffect(() => {
		if (!autoPlay || isPaused || totalSlides <= 1) return

		intervalRef.current = setInterval(() => {
			goToNext()
		}, autoPlayInterval) as NodeJS.Timeout

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
			}
		}
	}, [autoPlay, autoPlayInterval, isPaused, totalSlides, goToNext])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
			}
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current)
			}
		}
	}, [])

	return {
		currentIndex,
		isTransitioning,
		isPaused,
		goToNext,
		goToPrevious,
		goToSlide,
		pause,
		play,
		toggle,
		canGoNext,
		canGoPrevious,
	}
}

// Gesture handling hook for touch/swipe support
export interface UseCarouselGesturesOptions {
	onSwipeLeft?: () => void
	onSwipeRight?: () => void
	onSwipeUp?: () => void
	onSwipeDown?: () => void
	swipeThreshold?: number
	enabled?: boolean
}

export function useCarouselGestures({
	onSwipeLeft,
	onSwipeRight,
	onSwipeUp,
	onSwipeDown,
	swipeThreshold = 50,
	enabled = true,
}: UseCarouselGesturesOptions) {
	const touchStartX = useRef<number>(0)
	const touchStartY = useRef<number>(0)
	const touchEndX = useRef<number>(0)
	const touchEndY = useRef<number>(0)

	const handleTouchStart = useCallback(
		(e: React.TouchEvent) => {
			if (!enabled) return
			touchStartX.current = e.touches[0].clientX
			touchStartY.current = e.touches[0].clientY
		},
		[enabled],
	)

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (!enabled) return
			touchEndX.current = e.touches[0].clientX
			touchEndY.current = e.touches[0].clientY
		},
		[enabled],
	)

	const handleTouchEnd = useCallback(() => {
		if (
			!enabled ||
			!touchStartX.current ||
			!touchEndX.current ||
			!touchStartY.current ||
			!touchEndY.current
		)
			return

		const deltaX = touchStartX.current - touchEndX.current
		const deltaY = touchStartY.current - touchEndY.current
		const absDeltaX = Math.abs(deltaX)
		const absDeltaY = Math.abs(deltaY)

		// Determine if it's a horizontal or vertical swipe
		if (absDeltaX > absDeltaY && absDeltaX > swipeThreshold) {
			// Horizontal swipe
			if (deltaX > 0) {
				onSwipeLeft?.()
			} else {
				onSwipeRight?.()
			}
		} else if (absDeltaY > absDeltaX && absDeltaY > swipeThreshold) {
			// Vertical swipe
			if (deltaY > 0) {
				onSwipeUp?.()
			} else {
				onSwipeDown?.()
			}
		}

		// Reset touch values
		touchStartX.current = 0
		touchStartY.current = 0
		touchEndX.current = 0
		touchEndY.current = 0
	}, [
		enabled,
		swipeThreshold,
		onSwipeLeft,
		onSwipeRight,
		onSwipeUp,
		onSwipeDown,
	])

	return {
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
	}
}

// Keyboard navigation hook
export interface UseCarouselKeyboardOptions {
	onNext?: () => void
	onPrevious?: () => void
	onToggle?: () => void
	enabled?: boolean
}

export function useCarouselKeyboard({
	onNext,
	onPrevious,
	onToggle,
	enabled = true,
}: UseCarouselKeyboardOptions) {
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!enabled) return

			switch (e.key) {
				case 'ArrowLeft':
					e.preventDefault()
					onPrevious?.()
					break
				case 'ArrowRight':
					e.preventDefault()
					onNext?.()
					break
				case ' ':
				case 'Spacebar':
					e.preventDefault()
					onToggle?.()
					break
				case 'Home':
					e.preventDefault()
					// Could be used for going to first slide
					break
				case 'End':
					e.preventDefault()
					// Could be used for going to last slide
					break
			}
		},
		[enabled, onNext, onPrevious, onToggle],
	)

	return { handleKeyDown }
}
